/**
 * Dashboard Tab - Statistics Overview
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Business as SupplierIcon,
  Description as DeclarationIcon,
  CheckCircle as ActiveIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';

import { getDashboardStats, DashboardStats, getAlerts } from '../../../services/supplierDeclarationService';

const DashboardTab: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unacknowledgedAlertCount, setUnacknowledgedAlertCount] = useState<number>(0);

  useEffect(() => {
    loadStats();
    loadAlertCounts();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDashboardStats();
      setStats(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const loadAlertCounts = async () => {
    try {
      // Fetch unacknowledged alerts
      const alerts = await getAlerts(undefined, false, 0, 1000);
      setUnacknowledgedAlertCount(alerts.length);
    } catch (err: any) {
      console.error('Failed to load alert counts:', err);
      // Don't set error state, just log it
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!stats) return null;

  const statCards = [
    {
      title: 'Total Suppliers',
      value: stats.total_suppliers,
      icon: <SupplierIcon sx={{ fontSize: 40 }} />,
      color: '#0078d4',
      bgColor: 'rgba(0, 120, 212, 0.1)',
    },
    {
      title: 'Total Declarations',
      value: stats.total_declarations,
      icon: <DeclarationIcon sx={{ fontSize: 40 }} />,
      color: '#107c10',
      bgColor: 'rgba(16, 124, 16, 0.1)',
    },
    {
      title: 'Active',
      value: stats.active_declarations,
      icon: <ActiveIcon sx={{ fontSize: 40 }} />,
      color: '#107c10',
      bgColor: 'rgba(16, 124, 16, 0.1)',
    },
    {
      title: 'Expiring Soon',
      value: stats.expiring_soon,
      icon: <WarningIcon sx={{ fontSize: 40 }} />,
      color: '#ff8c00',
      bgColor: 'rgba(255, 140, 0, 0.1)',
    },
    {
      title: 'Expired',
      value: stats.expired,
      icon: <ErrorIcon sx={{ fontSize: 40 }} />,
      color: '#d13438',
      bgColor: 'rgba(209, 52, 56, 0.1)',
    },
    {
      title: 'Pending',
      value: stats.pending,
      icon: <PendingIcon sx={{ fontSize: 40 }} />,
      color: '#8661c5',
      bgColor: 'rgba(134, 97, 197, 0.1)',
    },
    {
      title: 'Unacknowledged Alerts',
      value: unacknowledgedAlertCount,
      icon: <WarningIcon sx={{ fontSize: 40 }} />,
      color: '#ff8c00',
      bgColor: 'rgba(255, 140, 0, 0.1)',
    },
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        Overview Statistics
      </Typography>

      <Grid container spacing={3}>
        {statCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
            <Card
              sx={{
                height: '100%',
                bgcolor: card.bgColor,
                border: `2px solid ${card.color}20`,
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ color: card.color }}>{card.icon}</Box>
                </Box>
                <Typography variant="h3" fontWeight={700} color={card.color}>
                  {card.value}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {card.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {stats.expiring_soon > 0 && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>{stats.expiring_soon}</strong> declaration(s) expiring soon. Review and request renewals.
          </Typography>
        </Alert>
      )}

      {stats.expired > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>{stats.expired}</strong> declaration(s) have expired. Immediate action required.
          </Typography>
        </Alert>
      )}

      {unacknowledgedAlertCount > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>{unacknowledgedAlertCount}</strong> unacknowledged alert(s). Review the Alerts tab.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default DashboardTab;
